let runtimePromise;

export function loadConverterHighstock(source) {
  if (window.Highcharts?.stockChart) return Promise.resolve(window.Highcharts);
  if (window.arzLoadHighstock) return window.arzLoadHighstock(source);
  if (runtimePromise) return runtimePromise;
  runtimePromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-converter-highstock="${source}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Highcharts), { once: true });
      existing.addEventListener('error', reject, { once: true });
      // The tag may have completed between the initial global check and this
      // lookup (another converter chart can request the same runtime).
      window.setTimeout(() => {
        if (window.Highcharts?.stockChart) resolve(window.Highcharts);
      }, 0);
      return;
    }
    const script = document.createElement('script');
    script.src = source;
    script.async = true;
    script.dataset.converterHighstock = source;
    script.addEventListener('load', () => resolve(window.Highcharts), { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.append(script);
  }).catch((error) => {
    runtimePromise = undefined;
    document.querySelector(`script[data-converter-highstock="${source}"]`)?.remove();
    throw error;
  });
  return runtimePromise;
}

function idle() {
  return new Promise((resolve) => {
    if (window.requestIdleCallback) window.requestIdleCallback(resolve, { timeout: 2000 });
    else window.setTimeout(resolve, 100);
  });
}

function waitForChartTrigger() {
  return new Promise((resolve) => {
    const start = () => {
      let timer;
      const finish = () => {
        window.removeEventListener('scroll', onScroll);
        window.clearTimeout(timer);
        resolve();
      };
      const onScroll = () => {
        if (window.scrollY > 0 || window.arzDigital?.data?.enable_scroll) finish();
      };
      // Match the shared /coins/ trigger without importing its legacy runtime.
      window.addEventListener('scroll', onScroll, { passive: true });
      timer = window.setTimeout(finish, 5000);
      if (window.scrollY > 0 || window.arzDigital?.data?.enable_scroll
          || document.cookie.split(';').some(cookie => cookie.trim().startsWith('_scroll='))) finish();
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  });
}

function chartColors() {
  const styles = getComputedStyle(document.documentElement);
  const line = styles.getPropertyValue('--arz-color-chart-neutral').trim()
    || styles.getPropertyValue('--arz-color-text-muted').trim()
    || '#64748b';
  return {
    line,
    stops: [
      [0, window.Highcharts.color(line).setOpacity(.24).get('rgba')],
      [1, window.Highcharts.color(line).setOpacity(0).get('rgba')],
    ],
  };
}

function extremes(points) {
  const values = points.map((point) => point[1]).filter(Number.isFinite);
  if (!values.length) return { min: 0, max: 1 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || Math.abs(max) * .02 || 1;
  return { min: min - range * .08, max: max + range * .12 };
}

function renderChart(container, points) {
  if (!container || !points.length || container.dataset.chartReady) return;
  container.dataset.chartReady = '1';
  container.classList.remove('arz-positive-chart', 'arz-negative-chart');
  container.classList.add('arz-neutral-chart');
  const colors = chartColors();
  const axis = extremes(points);
  window.Highcharts.stockChart(container.id, {
    chart: { height: 78, margin: [22, 0, 3, 0], panning: false },
    plotOptions: { series: { enableMouseTracking: false } },
    credits: { enabled: false },
    rangeSelector: { enabled: false },
    navigator: { enabled: false },
    scrollbar: { enabled: false },
    xAxis: { visible: false },
    yAxis: [{ visible: false, startOnTick: false, endOnTick: false, min: axis.min, max: axis.max }],
    tooltip: { enabled: false },
    series: [{
      type: 'areaspline',
      data: points,
      dataGrouping: { enabled: false },
      lineWidth: 1,
      color: colors.line,
      fillColor: { linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, stops: colors.stops },
      softThreshold: false,
      states: { hover: { lineWidth: 2 } },
    }],
  });
}

function normalizePoints(points) {
  return (Array.isArray(points) ? points : []).map((point) => [Number(point[0]), Number(point[1])])
    .filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]));
}

export async function initConverterHeaderCharts(root) {
  const header = document.querySelector('[data-arz-header-highstock-src]');
  if (!header || header.dataset.converterChartsReady) return;
  const tether = header.querySelector('#dollarPriceChart');
  const market = header.querySelector('#totalMarketcatChart');
  if (!tether && !market) return;
  header.dataset.converterChartsReady = '1';

  // Like /coins/, prepare market data before the chart runtime is triggered.
  const nexus = root.dataset.converterNexus || window.arzConstants?.NEXUS_BASEURL || '';
  const marketData = nexus && market
    ? fetch(`${nexus.replace(/\/$/, '')}/v1/pub/coins/chart/total-marketcap?range=1m`, {
      credentials: 'omit', headers: { Accept: 'application/json' },
    }).then(response => response.ok ? response.json() : null).catch(() => null)
    : Promise.resolve(null);

  try {
    await waitForChartTrigger();
    await loadConverterHighstock(header.dataset.arzHeaderHighstockSrc);
    await idle();
  } catch {
    delete header.dataset.converterChartsReady;
    return;
  }

  renderChart(tether, normalizePoints(window.dollarPriceChartData));

  if (!nexus || !market) return;
  try {
    const payload = await marketData;
    await idle();
    renderChart(market, normalizePoints(payload?.data));
  } catch {
    // The Tether chart remains available if the market-cap endpoint is temporarily unavailable.
  }
}
